import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  ToggleButton,
  ToggleButtonGroup,
  Chip,
  CircularProgress,
  Alert,
  Avatar,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import { ViewList, ViewModule, CheckCircle, HourglassEmpty, MailOutline } from '@mui/icons-material';
import { useQueryClient } from '@tanstack/react-query';
import { useShares, useShareMessages } from '../hooks/useShares';
import { Share, ShareMessage, ViewMode } from '../types/Share';
import { cloudKitService } from '../services/cloudkit';

interface MessageCardProps {
  message: ShareMessage;
  shareName: string;
  canApproveMessage: boolean;
  isActionLoading: boolean;
  onApproveMessage: (message: ShareMessage) => void;
}

function getDisplayNameInitial(name: string): string {
  const trimmed = name.trim();
  return trimmed ? trimmed.charAt(0).toUpperCase() : '?';
}

function MessageCard({
  message,
  shareName,
  canApproveMessage,
  isActionLoading,
  onApproveMessage,
}: MessageCardProps) {
  const formattedDate = new Date(message.createdTimestamp).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });

  const isApproved = message.shareMessageApprovedBool;

  return (
    <Card
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        border: '1px solid',
        borderColor: isApproved ? 'rgba(34,197,94,0.2)' : 'rgba(234,179,8,0.2)',
        borderLeft: '4px solid',
        borderLeftColor: isApproved ? '#22c55e' : '#eab308',
      }}
    >
      <CardContent sx={{ flexGrow: 1 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={1.5}>
          <Chip
            label={shareName}
            size="small"
            sx={{
              backgroundColor: '#f1f5f9',
              fontWeight: 600,
              fontSize: '0.75rem',
            }}
          />
          <Chip
            icon={isApproved ? <CheckCircle sx={{ fontSize: 14 }} /> : <HourglassEmpty sx={{ fontSize: 14 }} />}
            label={isApproved ? 'Approved' : 'Pending'}
            size="small"
            color={isApproved ? 'success' : 'warning'}
            variant="outlined"
            sx={{ fontWeight: 600, fontSize: '0.75rem' }}
          />
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
          <Avatar
            sx={{
              width: 28,
              height: 28,
              fontSize: '0.75rem',
              bgcolor: '#c7d2fe',
              color: '#4338ca',
            }}
          >
            {getDisplayNameInitial(message.shareMessageItemRequestedBy)}
          </Avatar>
          <Typography variant="body2" fontWeight={600}>
            {message.shareMessageItemRequestedBy || 'Unknown requester'}
          </Typography>
        </Box>

        {message.shareMessageContents && (
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{
              backgroundColor: '#f8fafc',
              p: 1.5,
              borderRadius: 2,
              lineHeight: 1.5,
              mb: 1.5,
            }}
          >
            {message.shareMessageContents}
          </Typography>
        )}

        {canApproveMessage && !isApproved && (
          <Button
            variant="contained"
            size="small"
            onClick={() => onApproveMessage(message)}
            disabled={isActionLoading}
            sx={{ mb: 1.5 }}
          >
            Approve
          </Button>
        )}

        <Typography variant="caption" color="text.secondary" display="block">
          {formattedDate}
        </Typography>
      </CardContent>
    </Card>
  );
}

export function MessageList() {
  const queryClient = useQueryClient();
  const { shares } = useShares();
  const { messages, isLoading, error } = useShareMessages();
  const [viewMode, setViewMode] = useState<ViewMode>('card');
  const [showOnlyMyShares, setShowOnlyMyShares] = useState(false);
  const [currentUserName, setCurrentUserName] = useState(cloudKitService.getUserName());
  const [selectedMessage, setSelectedMessage] = useState<ShareMessage | null>(null);
  const [isApproving, setIsApproving] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const [actionSuccess, setActionSuccess] = useState<string | null>(null);

  useEffect(() => {
    const handleAuthChange = (event: Event) => {
      const customEvent = event as CustomEvent<{ authenticated: boolean; userName: string }>;
      setCurrentUserName(customEvent.detail.userName || '');
    };
    window.addEventListener('cloudkit-auth-change', handleAuthChange);
    return () => window.removeEventListener('cloudkit-auth-change', handleAuthChange);
  }, []);

  const normalizedUserName = currentUserName.trim().toLowerCase();

  const shareMap = useMemo(() => {
    const map: Record<string, Share> = {};
    shares.forEach((share) => {
      map[share.recordName] = share;
    });
    return map;
  }, [shares]);

  const visibleMessages = useMemo(() => {
    if (!showOnlyMyShares) {
      return messages;
    }
    if (!normalizedUserName) {
      return [];
    }
    return messages.filter((message) => {
      const share = shareMap[message.shareParent];
      return (share?.itemSharedBy || '').trim().toLowerCase() === normalizedUserName;
    });
  }, [messages, showOnlyMyShares, normalizedUserName, shareMap]);

  const shareNameMap = useMemo(() => {
    const map: Record<string, string> = {};
    shares.forEach((share) => {
      map[share.recordName] = share.itemName;
    });
    return map;
  }, [shares]);

  const canApproveMessage = (message: ShareMessage): boolean => {
    if (!normalizedUserName) return false;
    return !message.shareMessageApprovedBool;
  };

  const handleViewChange = (_event: React.MouseEvent<HTMLElement>, newView: ViewMode | null) => {
    if (newView !== null) {
      setViewMode(newView);
    }
  };

  const handleMessageScopeChange = (_event: React.MouseEvent<HTMLElement>, value: string | null) => {
    if (value !== null) {
      setShowOnlyMyShares(value === 'mine');
    }
  };

  const handleApproveClick = (message: ShareMessage) => {
    setActionError(null);
    setActionSuccess(null);
    setSelectedMessage(message);
  };

  const handleApproveConfirm = async () => {
    if (!selectedMessage) return;
    setIsApproving(true);
    setActionError(null);
    setActionSuccess(null);

    try {
      await cloudKitService.approveShareMessage(selectedMessage.recordName);

      await queryClient.invalidateQueries({ queryKey: ['shareMessages'] });

      setActionSuccess(`Approved request from ${selectedMessage.shareMessageItemRequestedBy || 'requester'}.`);
      setSelectedMessage(null);
    } catch (actionError) {
      setActionError(actionError instanceof Error ? actionError.message : 'Failed to approve message');
    } finally {
      setIsApproving(false);
    }
  };

  const handleCloseDialog = () => {
    if (!isApproving) {
      setSelectedMessage(null);
    }
  };

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ m: 2 }}>
        Error loading messages: {error.message}
      </Alert>
    );
  }

  return (
    <Box sx={{ p: { xs: 2, md: 4 }, maxWidth: 1400, mx: 'auto' }}>
      <Box sx={{ mb: 3 }}>
        <Typography variant="h4" sx={{ mb: 0.5 }}>
          Messages
        </Typography>
        <Typography variant="body1" color="text.secondary">
          {visibleMessages.length} message{visibleMessages.length !== 1 ? 's' : ''}{' '}
          {showOnlyMyShares ? 'for your shares' : 'from the community'}
        </Typography>
      </Box>

      {actionSuccess && (
        <Alert severity="success" sx={{ mb: 2 }}>
          {actionSuccess}
        </Alert>
      )}
      {actionError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {actionError}
        </Alert>
      )}
      {!normalizedUserName && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Sign in to approve pending messages.
        </Alert>
      )}

      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 3,
          gap: 1.5,
          flexWrap: 'wrap',
        }}
      >
        <ToggleButtonGroup
          value={showOnlyMyShares ? 'mine' : 'all'}
          exclusive
          onChange={handleMessageScopeChange}
          size="small"
        >
          <ToggleButton value="mine">My Share Requests</ToggleButton>
          <ToggleButton value="all">All Messages</ToggleButton>
        </ToggleButtonGroup>

        <ToggleButtonGroup
          value={viewMode}
          exclusive
          onChange={handleViewChange}
          aria-label="view mode"
          size="small"
        >
          <ToggleButton value="card" aria-label="card view">
            <ViewModule />
          </ToggleButton>
          <ToggleButton value="table" aria-label="table view">
            <ViewList />
          </ToggleButton>
        </ToggleButtonGroup>
      </Box>

      {viewMode === 'card' ? (
        <Grid container spacing={2.5}>
          {visibleMessages.map((message) => (
            <Grid key={message.recordName} size={{ xs: 12, sm: 6, md: 4 }}>
              <MessageCard
                message={message}
                shareName={shareNameMap[message.shareParent] || 'Unknown'}
                canApproveMessage={canApproveMessage(message)}
                isActionLoading={isApproving}
                onApproveMessage={handleApproveClick}
              />
            </Grid>
          ))}
        </Grid>
      ) : (
        <TableContainer
          component={Paper}
          sx={{
            borderRadius: 3,
            border: '1px solid #e2e8f0',
            boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
            overflowX: 'auto',
            WebkitOverflowScrolling: 'touch',
          }}
        >
          <Table sx={{ minWidth: { xs: 500, md: 'auto' } }}>
            <TableHead>
              <TableRow>
                <TableCell>Share</TableCell>
                <TableCell>Requested By</TableCell>
                <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>Message</TableCell>
                <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>Date</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align="right">Action</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {visibleMessages.map((message) => (
                <TableRow key={message.recordName} hover>
                  <TableCell>
                    <Typography fontWeight={600} fontSize="0.9rem" noWrap>
                      {shareNameMap[message.shareParent] || 'Unknown'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                      <Avatar
                        sx={{
                          width: 24,
                          height: 24,
                          fontSize: '0.7rem',
                          bgcolor: '#c7d2fe',
                          color: '#4338ca',
                          display: { xs: 'none', sm: 'flex' },
                        }}
                      >
                        {getDisplayNameInitial(message.shareMessageItemRequestedBy)}
                      </Avatar>
                      <Typography variant="body2" noWrap>{message.shareMessageItemRequestedBy || 'Unknown'}</Typography>
                    </Box>
                  </TableCell>
                  <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>
                    <Typography variant="body2" color="text.secondary" sx={{ maxWidth: { sm: 200, md: 300 }, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {message.shareMessageContents || '-'}
                    </Typography>
                  </TableCell>
                  <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>
                    <Typography variant="body2" color="text.secondary" noWrap>
                      {new Date(message.createdTimestamp).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip
                      icon={message.shareMessageApprovedBool ? <CheckCircle sx={{ fontSize: 14 }} /> : <HourglassEmpty sx={{ fontSize: 14 }} />}
                      label={message.shareMessageApprovedBool ? 'Approved' : 'Pending'}
                      size="small"
                      color={message.shareMessageApprovedBool ? 'success' : 'warning'}
                      variant="outlined"
                      sx={{ fontWeight: 600, fontSize: '0.75rem' }}
                    />
                  </TableCell>
                  <TableCell align="right">
                    {canApproveMessage(message) && !message.shareMessageApprovedBool ? (
                      <Button
                        variant="contained"
                        size="small"
                        onClick={() => handleApproveClick(message)}
                        disabled={isApproving}
                      >
                        Approve
                      </Button>
                    ) : (
                      <Typography variant="body2" color="text.secondary">-</Typography>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {visibleMessages.length === 0 && !isLoading && (
        <Box
          textAlign="center"
          py={8}
          sx={{
            borderRadius: 4,
            backgroundColor: '#ffffff',
            border: '2px dashed #e2e8f0',
            mt: 2,
          }}
        >
          <MailOutline sx={{ fontSize: 56, color: '#c7d2fe', mb: 2 }} />
          <Typography variant="h6" color="text.secondary" sx={{ mb: 0.5 }}>
            No messages yet
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {showOnlyMyShares
              ? 'Requests for your shares will appear here.'
              : 'Messages from the community will appear here.'}
          </Typography>
        </Box>
      )}

      <Dialog open={selectedMessage !== null} onClose={handleCloseDialog}>
        <DialogTitle>Approve Pending Message?</DialogTitle>
        <DialogContent>
          <Typography>
            This will mark the message as approved.
          </Typography>
          <Typography sx={{ mt: 1.5 }} color="text.secondary">
            Requester: <strong>{selectedMessage?.shareMessageItemRequestedBy || 'Unknown'}</strong>
          </Typography>
          <Typography color="text.secondary">
            Share: <strong>{selectedMessage ? (shareNameMap[selectedMessage.shareParent] || 'Unknown') : ''}</strong>
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={handleCloseDialog} disabled={isApproving} variant="outlined">
            Cancel
          </Button>
          <Button onClick={handleApproveConfirm} disabled={isApproving} variant="contained">
            {isApproving ? 'Approving...' : 'Approve'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
